import React, { useRef, useState, useEffect } from "react";
import { FiX, FiCheck, FiRefreshCcw, FiRotateCcw } from "react-icons/fi";
import "./ImageDrawer.css";

const ImageDrawer = ({ imageUrl, onClose, onSave }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [ctx, setCtx] = useState(null);
    const [imageObj, setImageObj] = useState(null);
    const [brushSize, setBrushSize] = useState(10);
    const [brushColor, setBrushColor] = useState("#FF0000");
    const [history, setHistory] = useState([]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        setCtx(context);

        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
            setImageObj(img);
            const container = containerRef.current;
            if (!container) return;
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;

            let width = img.width;
            let height = img.height;

            if (width > containerWidth || height > containerHeight) {
                const ratio = Math.min(containerWidth / width, containerHeight / height);
                width = width * ratio;
                height = height * ratio;
            }

            canvas.width = width;
            canvas.height = height;

            context.drawImage(img, 0, 0, width, height);

            context.lineCap = "round";
            context.lineJoin = "round";
            context.lineWidth = brushSize;
            context.strokeStyle = brushColor;
            
            setHistory([canvas.toDataURL()]);
        };
        img.onerror = () => {
            // Fallback if CORS fails: you could try fetching as blob here if needed
            console.error("Failed to load image for drawing due to CORS or network error.");
        };
        img.src = imageUrl;
    }, [imageUrl]);

    useEffect(() => {
        if (ctx) {
            ctx.lineWidth = brushSize;
            ctx.strokeStyle = brushColor;
        }
    }, [brushSize, brushColor, ctx]);

    const getCoordinates = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        
        // Scale handles mismatch between CSS size and canvas coordinate system
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        if (event.touches && event.touches.length > 0) {
            return {
                offsetX: (event.touches[0].clientX - rect.left) * scaleX,
                offsetY: (event.touches[0].clientY - rect.top) * scaleY
            };
        }

        return {
            offsetX: (event.clientX - rect.left) * scaleX,
            offsetY: (event.clientY - rect.top) * scaleY
        };
    };

    const startDrawing = (e) => {
        if (e.cancelable) e.preventDefault(); // Prevent touch scrolling
        const { offsetX, offsetY } = getCoordinates(e);
        ctx.beginPath();
        ctx.moveTo(offsetX, offsetY);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        if (e.cancelable) e.preventDefault();
        const { offsetX, offsetY } = getCoordinates(e);
        ctx.lineTo(offsetX, offsetY);
        ctx.stroke();
    };

    const saveHistoryState = () => {
        const canvas = canvasRef.current;
        if (canvas) {
            setHistory(prev => [...prev, canvas.toDataURL()]);
        }
    };

    const stopDrawing = () => {
        if (isDrawing) {
            ctx.closePath();
            setIsDrawing(false);
            saveHistoryState();
        }
    };

    const handleUndo = () => {
        if (history.length > 1) {
            const newHistory = [...history];
            newHistory.pop(); // remove current state
            const prevState = newHistory[newHistory.length - 1];
            setHistory(newHistory);
            
            const img = new Image();
            img.src = prevState;
            img.onload = () => {
                if (ctx) {
                    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                    ctx.drawImage(img, 0, 0);
                }
            };
        }
    };

    const handleClear = () => {
        if (ctx && imageObj) {
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            ctx.drawImage(imageObj, 0, 0, canvasRef.current.width, canvasRef.current.height);
            saveHistoryState();
        }
    };

    const handleSave = () => {
        const canvas = canvasRef.current;
        try {
            canvas.toBlob((blob) => {
                if (!blob) {
                    console.error("Canvas toBlob failed");
                    return;
                }
                const file = new File([blob], "drawn_mask.png", { type: "image/png" });
                const previewUrl = canvas.toDataURL("image/png");
                onSave(file, previewUrl);
                onClose();
            }, "image/png");
        } catch (err) {
            console.error("Error saving drawn image (possibly tainted canvas):", err);
            alert("Could not save drawing. The image origin does not allow exporting.");
        }
    };

    return (
        <div className="image-drawer-overlay">
            <div className="image-drawer-modal">
                <div className="image-drawer-header">
                    <h3 style={{ margin: 0, color: "white" }}>Draw on Image</h3>
                    <div className="drawer-controls">
                        <input
                            type="color"
                            value={brushColor}
                            onChange={(e) => setBrushColor(e.target.value)}
                            title="Brush Color"
                        />
                        <input
                            type="range"
                            min="2"
                            max="50"
                            value={brushSize}
                            onChange={(e) => setBrushSize(e.target.value)}
                            title="Brush Size"
                        />
                        <button onClick={handleUndo} className="drawer-btn clear-btn" disabled={history.length <= 1} style={{ opacity: history.length <= 1 ? 0.5 : 1 }}>
                            <FiRotateCcw /> Undo
                        </button>
                        <button onClick={handleClear} className="drawer-btn clear-btn">
                            <FiRefreshCcw /> Clear
                        </button>
                    </div>
                </div>

                <div className="image-drawer-body" ref={containerRef}>
                    <canvas
                        ref={canvasRef}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        style={{ touchAction: "none" }}
                    />
                </div>

                <div className="image-drawer-footer">
                    <button onClick={onClose} className="drawer-btn cancel-btn">
                        <FiX /> Cancel
                    </button>
                    <button onClick={handleSave} className="drawer-btn save-btn">
                        <FiCheck /> Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageDrawer;
